import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  IconButton,
  useTheme,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ReplayIcon from "@mui/icons-material/Replay";
import { logout } from "../auth/helper";
import { clearError } from "../../redux/slices/ErrorSlice";
import { RootState } from "../../redux/store";

export const ErrorBoundary = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { error } = useSelector((state: RootState) => state.Error);

  if (!error?.message) {
    return null;
  }

  const { title, message, shouldLogout } = error;

  const handleLogout = () => {
    dispatch(clearError());
    logout(dispatch);
  };

  const handleDismiss = () => {
    dispatch(clearError());
  };

  const handleRetry = () => {
    dispatch(clearError());
    window.location.reload();
  };

  return shouldLogout ? (
    <Dialog
      fullScreen={fullScreen}
      open={!!error}
      onClose={handleLogout}
      aria-labelledby="logout-dialog-title"
    >
      <DialogTitle id="logout-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleLogout}
          color="primary"
          variant="contained"
          autoFocus
        >
          Log Out
        </Button>
      </DialogActions>
    </Dialog>
  ) : (
    <Snackbar
      open={!!error}
      onClose={(_, reason) => {
        if (reason === "clickaway") return;
        handleDismiss();
      }}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Alert
        onClose={handleDismiss}
        severity="error"
        variant="filled"
        action={
          <IconButton
            aria-label="retry"
            color="inherit"
            size="small"
            onClick={handleRetry}
          >
            <ReplayIcon fontSize="inherit" />
          </IconButton>
        }
        sx={{ width: "100%", alignItems: "center" }}
      >
        {title}, {message}
      </Alert>
    </Snackbar>
  );
};
